import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useEffect, useState } from 'react';

/**
 * Returns true if the current URL looks like we're returning from an OAuth redirect
 * (MSAL should handle it; we must not call loginRedirect again).
 */
function isReturningFromRedirect() {
  const { search, hash } = window.location;
  const params = new URLSearchParams(search || hash.replace(/^#?/, ''));
  return params.has('code') || params.has('state') || params.has('error');
}

/**
 * Reads error and error_description from the URL (after redirect from Microsoft).
 */
function getRedirectError() {
  const { search, hash } = window.location;
  const params = new URLSearchParams(search || hash.replace(/^#?/, ''));
  const error = params.get('error');
  const description = params.get('error_description');
  if (!error) return null;
  return { error, description: description || error };
}

/**
 * Renders children only when the user is signed in with Microsoft Entra.
 * Otherwise triggers redirect login or shows a loading state.
 */
export default function AuthGuard({ children }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [redirectError, setRedirectError] = useState(null);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || isAuthenticated) return;
    if (isReturningFromRedirect()) {
      const err = getRedirectError();
      if (err) setRedirectError(err);
      return;
    }
    instance.loginRedirect();
  }, [isAuthenticated, inProgress, instance]);

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-content">
          <div className="auth-loading-spinner" />
          <p>Signing you in with Microsoft…</p>
        </div>
      </div>
    );
  }

  if (redirectError) {
    const isAccessDenied = redirectError.error === 'access_denied' || /access_denied|AADSTS50105/i.test(redirectError.description || '');
    return (
      <div className="auth-loading">
        <div className="auth-loading-content">
          <p className="auth-error-title">Sign-in failed</p>
          <p className="auth-error-description">{redirectError.description || redirectError.error}</p>
          {isAccessDenied && (
            <p className="auth-error-hint">
              Your account may not be assigned to this app. Ask your admin to add you in Azure: Entra ID → App registrations → this app → Enterprise applications → Users and groups.
            </p>
          )}
          <button
            type="button"
            className="auth-retry-btn"
            onClick={() => {
              setRedirectError(null);
              window.history.replaceState({}, document.title, window.location.pathname);
              instance.loginRedirect();
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-content">
          <div className="auth-loading-spinner" />
          <p>Redirecting to sign in…</p>
        </div>
      </div>
    );
  }

  return children;
}
